import { useCallback, useEffect, useRef, useState } from "react";
import { StickyNote, X } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";
import { MyTodos } from "@/components/MyTodos";

/**
 * Floating "Personal Notes + To-do" widget.
 * A fixed circular button sits above the chat FAB. Clicking it opens a compact
 * panel with two panes: a free-form notepad and a checklist. Everything is
 * private to the caller — the API scopes both collections by `user_id`.
 */
export default function PersonalNotesWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("notes"); // notes | todos
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const noteRef = useRef(note);

  const loadAll = useCallback(async () => {
    try {
      const n = await api.get("/notes");
      setNote(n.data?.text || "");
      noteRef.current = n.data?.text || "";
    } catch (_e) { /* silent on first load; user can retry via reopen */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { if (open && !loaded) loadAll(); }, [open, loaded, loadAll]);
  useEffect(() => {
    const count = async () => { try { const { data } = await api.get("/my-day/todos"); setPending(data.filter((t) => !t.done).length); } catch { /* badge only */ } };
    count(); window.addEventListener("lim:todos-changed", count);
    return () => window.removeEventListener("lim:todos-changed", count);
  }, []);

  // Debounced save for the notepad
  useEffect(() => {
    if (!open || !loaded) return;
    if (note === noteRef.current) return;
    const id = setTimeout(async () => {
      try {
        setSaving(true);
        await api.put("/notes", { text: note });
        noteRef.current = note;
      } catch {
        toast.error("Note not saved");
      } finally { setSaving(false); }
    }, 600);
    return () => clearTimeout(id);
  }, [note, open, loaded]);

  return (
    <>
      {/* FAB — sits ~140px above bottom to clear the chat FAB and bottom nav */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notes-fab"
        aria-label="Personal notes & to-do"
        className={`fixed right-6 z-40 h-11 w-11 rounded-full flex items-center justify-center border shadow-lg transition-transform ${
          open
            ? "bg-white text-black border-white/40 rotate-90"
            : "bg-surface text-white border-white/10 hover:scale-105"
        } bottom-[9rem] lg:bottom-[8.5rem]`}
      >
        {open ? <X size={16} /> : <StickyNote size={16} />}
        {!open && pending > 0 && (
          <span className="absolute -top-1 -right-1 text-[0.65rem] font-mono bg-[#E11D2E] text-white rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
            {pending}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="notes-panel"
          className="fixed right-6 bottom-[13rem] lg:bottom-[12.5rem] z-50 w-[92vw] sm:w-[380px] max-h-[70vh] rounded-xl border border-white/10 bg-surface shadow-2xl shadow-black/60 flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <div>
              <div className="overline">Private Workspace</div>
              <div className="text-sm font-display">Notes & To-do</div>
            </div>
            <div className="inline-flex rounded-full border border-white/10 p-0.5 bg-black/40">
              <button
                onClick={() => setTab("notes")}
                data-testid="notes-tab-notes"
                className={`text-[0.7rem] uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  tab === "notes" ? "bg-white text-black" : "text-white/60"
                }`}
              >Notes</button>
              <button
                onClick={() => setTab("todos")}
                data-testid="notes-tab-todos"
                className={`text-[0.7rem] uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  tab === "todos" ? "bg-white text-black" : "text-white/60"
                }`}
              >To-do {pending > 0 && <span className="ml-1 text-[0.65rem] font-mono">{pending}</span>}</button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {tab === "notes" ? (
              <div>
                <textarea
                  data-testid="notes-textarea"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Jot down anything — client contacts, ideas, follow-ups. Only you can see this."
                  className="w-full min-h-[240px] bg-black/40 border border-white/10 rounded-md p-3 text-sm text-white/85 focus:outline-none focus:ring-2 focus:ring-red-500 leading-relaxed"
                />
                <div className="text-[0.7rem] text-white/40 mt-2 font-mono">
                  {saving ? "Saving…" : "Autosaved · private to you"}
                </div>
              </div>
            ) : (
              <div>
                <MyTodos compact onCount={setPending} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
